import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DescriptionIcon from "@mui/icons-material/Description";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";

import { aiNotesExtract } from "../../../../../utils/services/codingOpportunitiesAiClient";
import { flattenNotesToText } from "../../../IAsystem/mappers/notes.flatten";
import { formatDate } from "../../../../../utils/formatters/date";

// Adapta la respuesta del backend (`/ai/notes-extract`) al shape que consume
// la UI de este tab y el combiner de IAAssistTab: {findings, medications,
// summary}. El backend devuelve `medications[]` con (medication, active,
// treats, icd10_hint, source_note_id, date, confidence) — ver
// 08_AI_BACKEND_ENDPOINTS.md §2.2.
const adaptNotesExtractResponse = (response) => ({
  findings: (response?.key_findings || []).map((f) => ({
    condition: f.condition,
    evidence: f.evidence,
    confidence: f.confidence,
    negated: !!f.negated,
    icd10_relevance: f.icd10_relevance || null,
  })),
  medications: (response?.medications || []).map((m) => ({
    medication: m.medication,
    active: m.active !== false,
    treats: m.treats || "unknown",
    icd10_hint: m.icd10_hint || null,
    source_note_id: m.source_note_id || null,
    date: m.date || null,
    confidence: typeof m.confidence === "number" ? m.confidence : null,
  })),
  summary: response?.summary || "",
  coding_relevance: response?.coding_relevance || [],
});

const NOTE_TYPES = [
  { key: "followUpExam", label: "Follow Up Exam 2.0", color: "primary" },
  { key: "annualWellness", label: "Annual Wellness 2.0", color: "success" },
  { key: "telehealth", label: "TELEHEALTH_2.0", color: "info" },
  { key: "hospitalDischarge", label: "Hospital Discharge Follow Up", color: "warning" },
];

export default function ClinicalNotesTab({
  patient,
  notesByType,
  loading,
  notesAnalysisPerNote,
  setNotesAnalysisPerNote,
}) {
  const [selectedNote, setSelectedNote] = useState(null);
  const [analysisDialog, setAnalysisDialog] = useState(null);
  const [runningNoteId, setRunningNoteId] = useState(null);
  const [error, setError] = useState(null);

  // State for bulk analysis
  const [bulkAnalyzing, setBulkAnalyzing] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });

  const totalNotes = NOTE_TYPES.reduce(
    (acc, type) => acc + (notesByType?.[type.key]?.length || 0),
    0
  );

  const analyzedCount = Object.keys(notesAnalysisPerNote || {}).length;

  // Get all notes pending analysis
  const getAllPendingNotes = () => {
    const allNotes = [];
    NOTE_TYPES.forEach((type) => {
      const notes = notesByType?.[type.key] || [];
      notes.forEach((note) => {
        const noteId = note.noteId || note.id;
        if (!notesAnalysisPerNote?.[noteId]) {
          allNotes.push(note);
        }
      });
    });
    return allNotes;
  };

  const pendingNotesCount = getAllPendingNotes().length;

  // Analyze a single note
  const analyzeNote = async (note) => {
    const noteId = note.noteId || note.id || `note_${Date.now()}`;
    try {
      setError(null);
      setRunningNoteId(noteId);

      const notesText = flattenNotesToText([note]);

      console.log("=== ANALYZING CLINICAL NOTE ===");
      console.log("Note ID:", noteId);
      console.log("Note Type:", note.__group);
      console.log("Note has fields:", !!note.fields);
      console.log("Fields keys:", note.fields ? Object.keys(note.fields) : []);
      console.log("================================");

      const response = await aiNotesExtract({
        cipId: patient?.cipId,
        patient,
        notesText,
      });

      const adapted = adaptNotesExtractResponse(response);

      const analysisResult = {
        ...adapted,
        noteId,
        noteType: note.__group,
        noteDate: note.noteDateTime || note.noteDate || note.date,
        noteProvider: note.providerName || note.provider,
      };

      setNotesAnalysisPerNote((prev) => ({
        ...prev,
        [noteId]: analysisResult,
      }));

      console.log("=== Note analysis completed ===");
      console.log("Note ID:", noteId);
      console.log("Note Type:", note.__group);
      console.log("Analysis Result:", analysisResult);

    } catch (e) {
      setError(e?.message || String(e));
      return null;
    } finally {
      setRunningNoteId(null);
    }
  };

  // Analyze all pending notes
  const analyzeAllNotes = async () => {
    const pendingNotes = getAllPendingNotes();
    if (pendingNotes.length === 0) return;

    setBulkAnalyzing(true);
    setBulkProgress({ current: 0, total: pendingNotes.length });
    setError(null);

    console.log("=== STARTING BULK ANALYSIS ===");
    console.log("Total notes to analyze:", pendingNotes.length);

    const results = {};
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < pendingNotes.length; i++) {
      const note = pendingNotes[i];
      const noteId = note.noteId || note.id || `note_${i}`;

      setBulkProgress({ current: i + 1, total: pendingNotes.length });
      setRunningNoteId(noteId);

      try {
        console.log(`Analyzing note ${i + 1}/${pendingNotes.length}: ${noteId}`);

        const response = await aiNotesExtract({
          cipId: patient?.cipId,
          patient,
          notesText: flattenNotesToText([note]),
        });

        const adapted = adaptNotesExtractResponse(response);

        const analysisResult = {
          ...adapted,
          noteId,
          noteType: note.__group,
          noteDate: note.noteDateTime || note.noteDate || note.date,
          noteProvider: note.providerName || note.provider,
        };
        results[noteId] = analysisResult;
        successCount++;
      } catch (e) {
        errorCount++;
        console.error(`Error analyzing note ${noteId}:`, e);
        // Si se topó con rate limit, no seguir martillando.
        if (e?.status === 429) {
          setError(e.message);
          break;
        }
      }
    }

    // Update state with all results
    setNotesAnalysisPerNote((prev) => ({
      ...prev,
      ...results,
    }));

    console.log("=== BULK ANALYSIS COMPLETED ===");
    console.log("Successful:", successCount);
    console.log("Errors:", errorCount);

    if (errorCount > 0) {
      setError(`Completed with ${errorCount} error(s). ${successCount} notes analyzed successfully.`);
    }

    setBulkAnalyzing(false);
    setRunningNoteId(null);
    setBulkProgress({ current: 0, total: 0 });
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 2 }}>
        <CircularProgress size={18} />
        <Typography variant="body2">Loading clinical notes...</Typography>
      </Box>
    );
  }

  if (totalNotes === 0) {
    return (
      <Alert severity="info">
        No clinical notes found for this patient in AdvancedMD.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header con estado */}
      <Paper sx={{ p: 2, mb: 2, bgcolor: "grey.50" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Clinical Notes Analysis
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Analyze each note to extract findings for Coding AI Assist
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Chip
              label={`${totalNotes} total notes`}
              variant="outlined"
            />
            <Chip
              icon={analyzedCount > 0 ? <CheckCircleIcon /> : undefined}
              label={`${analyzedCount} analyzed`}
              color={analyzedCount > 0 ? "success" : "default"}
              variant={analyzedCount > 0 ? "filled" : "outlined"}
            />
          </Stack>
        </Stack>

        {analyzedCount === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Analyze at least one note to enable the "Coding AI Assist" tab.
          </Alert>
        )}

        {analyzedCount > 0 && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {analyzedCount} note(s) analyzed. You can now use the "Coding AI Assist" tab.
          </Alert>
        )}

        {/* Botón para analizar todas las notas */}
        <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            variant="contained"
            color="secondary"
            startIcon={bulkAnalyzing ? <CircularProgress size={18} color="inherit" /> : <PlaylistAddCheckIcon />}
            onClick={analyzeAllNotes}
            disabled={bulkAnalyzing || pendingNotesCount === 0}
          >
            {bulkAnalyzing
              ? `Analyzing ${bulkProgress.current} of ${bulkProgress.total}...`
              : pendingNotesCount === 0
              ? "All Notes Analyzed"
              : `Analyze All Notes (${pendingNotesCount} pending)`}
          </Button>

          {bulkAnalyzing && (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Please wait, this may take a moment...
            </Typography>
          )}
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Notas por tipo */}
      {NOTE_TYPES.map((type) => {
        const notes = notesByType?.[type.key] || [];
        if (notes.length === 0) return null;

        return (
          <Accordion key={type.key} defaultExpanded={notes.length > 0 && notes.length <= 5}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={2} alignItems="center">
                <DescriptionIcon color={type.color} />
                <Typography fontWeight={700}>{type.label}</Typography>
                <Chip
                  size="small"
                  label={`${notes.length} note${notes.length !== 1 ? "s" : ""}`}
                  color={type.color}
                  variant="outlined"
                  onClick={(e) => e.stopPropagation()}
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Provider</TableCell>
                    <TableCell>AI Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {notes.map((note, idx) => {
                    const noteId = note.noteId || note.id || `note_${idx}`;
                    const hasAnalysis = !!notesAnalysisPerNote?.[noteId];
                    const isAnalyzing = runningNoteId === noteId;

                    return (
                      <TableRow key={noteId} hover>
                        <TableCell>
                          {formatDate(note.noteDateTime || note.noteDate || note.date)}
                        </TableCell>
                        <TableCell>{note.providerName || note.provider || "—"}</TableCell>
                        <TableCell>
                          {hasAnalysis ? (
                            <Chip
                              size="small"
                              icon={<CheckCircleIcon />}
                              label="Analyzed"
                              color="success"
                            />
                          ) : (
                            <Chip size="small" label="Pending" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            {/* Ver Nota */}
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<VisibilityIcon />}
                              onClick={() => setSelectedNote(note)}
                            >
                              View Note
                            </Button>

                            {/* Analizar con IA */}
                            {!hasAnalysis && (
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                startIcon={isAnalyzing ? <CircularProgress size={14} color="inherit" /> : <SmartToyIcon />}
                                onClick={() => analyzeNote(note)}
                                disabled={isAnalyzing || bulkAnalyzing}
                              >
                                {isAnalyzing ? "Analyzing..." : "Analyze"}
                              </Button>
                            )}

                            {/* Ver Resultado del Análisis */}
                            {hasAnalysis && (
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                startIcon={<SmartToyIcon />}
                                onClick={() => setAnalysisDialog({ note, analysis: notesAnalysisPerNote[noteId] })}
                              >
                                View AI Result
                              </Button>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* Dialog para ver el contenido de la nota */}
      <Dialog
        open={!!selectedNote}
        onClose={() => setSelectedNote(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 900 }}>
          Clinical Note - {selectedNote?.__group || "Note"}
        </DialogTitle>
        <DialogContent dividers>
          {selectedNote && (
            <Box>
              <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
                <Chip
                  size="small"
                  label={`Date: ${formatDate(selectedNote.noteDateTime || selectedNote.noteDate)}`}
                  variant="outlined"
                />
                {selectedNote.providerName && (
                  <Chip
                    size="small"
                    label={`Provider: ${selectedNote.providerName}`}
                    variant="outlined"
                  />
                )}
                {selectedNote.templateName && (
                  <Chip
                    size="small"
                    label={`Template: ${selectedNote.templateName}`}
                    variant="outlined"
                  />
                )}
              </Stack>

              <Paper variant="outlined" sx={{ p: 2, maxHeight: 500, overflow: "auto" }}>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}
                >
                  {flattenNotesToText([selectedNote])}
                </Typography>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedNote(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para ver el análisis de IA */}
      <Dialog
        open={!!analysisDialog}
        onClose={() => setAnalysisDialog(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 900 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SmartToyIcon color="success" />
            <span>AI Analysis - {analysisDialog?.note?.__group}</span>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {analysisDialog && (
            <Box>
              <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
                <Chip
                  size="small"
                  label={`Date: ${formatDate(analysisDialog.note?.noteDateTime || analysisDialog.note?.noteDate)}`}
                  variant="outlined"
                />
                {analysisDialog.note?.providerName && (
                  <Chip size="small" label={`Provider: ${analysisDialog.note.providerName}`} variant="outlined" />
                )}
              </Stack>

              {/* Summary */}
              <Typography fontWeight={700} sx={{ mb: 1 }}>
                Summary
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, opacity: 0.85 }}>
                {analysisDialog.analysis?.summary || "No summary available."}
              </Typography>

              {/* Findings */}
              {analysisDialog.analysis?.findings?.length > 0 && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography fontWeight={700} sx={{ mb: 1 }}>
                    Clinical Findings ({analysisDialog.analysis.findings.length})
                  </Typography>
                  <Stack spacing={1}>
                    {analysisDialog.analysis.findings.map((f, idx) => (
                      <Paper key={idx} variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ mb: 0.5 }}>
                          <Chip size="small" label={f.condition || "Finding"} color="primary" />
                          <Chip
                            size="small"
                            label={`${Math.round((Number(f.confidence || 0) * 100))}%`}
                            variant="outlined"
                          />
                          {f.icd10_relevance && (
                            <Chip size="small" label={`ICD-10: ${f.icd10_relevance}`} color="info" variant="outlined" />
                          )}
                          {f.negated && (
                            <Chip size="small" label="Negated/Ruled Out" color="warning" variant="outlined" />
                          )}
                        </Stack>
                        <Typography variant="body2" sx={{ opacity: 0.85, fontStyle: "italic" }}>
                          "{f.evidence}"
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>
                </>
              )}

              {/* Medications */}
              {analysisDialog.analysis?.medications?.length > 0 && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography fontWeight={700} sx={{ mb: 1 }}>
                    Medications
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {analysisDialog.analysis.medications.map((m, idx) => (
                      <Chip
                        key={idx}
                        label={`${m.medication} → ${m.treats}`}
                        variant="outlined"
                        size="small"
                      />
                    ))}
                  </Stack>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnalysisDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
